import { useState } from "react";
import { Linkedin } from "react-feather";
import { motion, AnimatePresence } from "framer-motion";

const TeamCard = ({ member }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isExecutive = member.executive;

  return (
    <>
      {/* Card */}
      <div
        onMouseEnter={() => setIsHovered(member?.modal ? true : false)}
        onMouseLeave={() => setIsHovered(false)}
        className={`relative flex-1 group rounded-3xl p-8 border overflow-hidden cursor-pointer shadow-xl transition-transform duration-300 
          ${
            isExecutive
              ? "bg-gradient-to-br from-white/60 via-white/20 to-gray-100/30 border-gray-100"
              : "bg-gradient-to-br from-white/60 via-white/20 to-gray-100/30 border-gray-100"
          }`}
      >
        {/* Glow */}
        <div className="absolute inset-0 rounded-3xl z-0 bg-gradient-to-tr from-purple-300/10 via-transparent to-blue-200/10 blur-2xl scale-[1.3]" />
        {isExecutive && (
          <div className="absolute -top-16 -left-16 w-60 h-60 bg-purple-400 opacity-20 blur-3xl rounded-full z-0" />
        )}

        {/* Image */}
        <div className="relative z-10 w-24 h-24 mx-auto mb-6 rounded-full overflow-hidden border-4 border-white shadow-lg">
          <img
            src={member.img}
            alt={member.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Badge */}
        {isExecutive && (
          <div className="absolute top-4 left-4 z-10 text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wide bg-gradient-to-r from-purple-600 to-indigo-500 text-white shadow-md">
            {member.position}
          </div>
        )}

        {/* Name & Position */}
        <h3 className="relative z-10 text-lg font-semibold text-center text-gray-900">
          {member.name}
        </h3>
        <p
          className={`relative z-10 text-sm text-center font-medium ${
            isExecutive ? "text-purple-600" : "text-blue-600"
          }`}
        >
          {member.position}
        </p>

        {/* Short Desc */}
        {member.shortDesc && (
          <p className="relative z-10 text-xs text-center text-gray-600 italic mt-3">
            {member.shortDesc}
          </p>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
            className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
          >
            <div className="pointer-events-auto w-full max-w-5xl px-4">
              <div className="relative bg-white/70 backdrop-blur-xl border border-white/30 rounded-3xl shadow-2xl p-10 grid md:grid-cols-[1fr_2fr] gap-10 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-[400px] h-[400px] bg-purple-300/30 rounded-full blur-[120px] z-0" />

                {/* Left: Profile info */}
                <div className="relative z-10 flex flex-col items-center md:items-start text-center md:text-left">
                  <img
                    src={member.img}
                    alt={member.name}
                    className="w-28 h-28 rounded-full border-4 border-white shadow-md object-cover"
                  />
                  <h2 className="mt-4 text-xl font-semibold text-gray-900">
                    {member.name}
                  </h2>
                  <p className="text-sm font-medium text-gray-700">
                    {member.position}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    With Labops since {member.experienceYears}
                  </p>

                  {member.showLinkdin && member.linkedInUrl && (
                    <a
                      href={member.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white bg-[#0077b5] hover:bg-[#005885] transition-all shadow"
                    >
                      <Linkedin size={16} />
                      LinkedIn Profile
                    </a>
                  )}
                </div>

                {/* Right: Story */}
                <div className="relative z-10 text-gray-800 space-y-4">
                  <div className="text-base font-semibold text-purple-700">
                    {member.shortDesc}
                  </div>
                  <p className="text-sm leading-relaxed">{member.longDesc}</p>
                  <div className="mt-4 border-t pt-4 text-xs text-gray-500">
                    Currently leading the AI transformation of Indian healthcare
                    through next-gen tools and strategic partnerships at Labops.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TeamCard;
